/**
 * Undo and "Not done after all" for everything that is checked off (docs/DESIGN.md §3b).
 * Owner, 2026-09-28: "If they check off an item we need a grace (undo) period, so if they made a mistake they can press
 * the button or revert it for later."
 *
 * Two different things, on purpose:
 *  1. **Undo (the window).** Every check-off shows "Done ✓ · Undo" for UNDO_SECONDS (30 s), and on the phone a shake offers
 *     the same Undo (owner, 2026-10-09). Undo inside the window VOIDS the event, it never deletes it: a correction with
 *     reason "undone" (who, when, seconds after) is appended, so it never counts, never reaches notices, nags or Insights,
 *     and the history and audit trail still show "Undone by Sam 12 s later". The record is written at once (server time,
 *     durable, seen by the team) and the server voids it on Undo only for the same account (or a manager) and only
 *     within UNDO_SERVER_SECONDS (window + 5 s grace). A tap still in the offline queue is simply dropped.
 *  2. **Not done after all (a correction, never a deletion).** After the window the record is part of the food-safety
 *     record. The person who did it (the same day) or a manager (any time within the record's retention) appends a
 *     correction: who, when, why. The original stays, struck through with the correction beside it; the item is due
 *     again; the binder shows both; Insights count it as not done.
 *
 * Gates (packages/core/test/corrections.test.ts):
 * | U1 Undo leaves no trace | Within 30 s, the same account: the row is removed (the audit log keeps who undid what). |
 * | U2 Undo is not a delete button | After 30 s, or another account: refused, and "Not done after all" is offered. |
 * | U3 Undo only what nothing else read | A reading outside its limit (a learning moment was written from it) or a receiving
 *   check (it marked orders arrived) is corrected, never undone. |
 * | R1 Honest record | A correction never removes or edits the original; a second correction is refused. |
 * | R2 Who may correct | The person who did it, the same local day; a manager or owner any time within retention. Another
 *   staff member: never. |
 * | R3 Corrected means not done | `live()` drops corrected rows for every "is it done?" reader (due, nags, Insights,
 *   Places state, chores, the order list); the binder and the history show them struck through. |
 */
import type { Id, IsoDateTime, Role } from "./model.ts";

/**
 * The toast (and a shake on the phone) offers Undo this long. Owner, 2026-10-09: "you should do a 30 seconds shake to
 * undo a logging … because its easy to make a mistake."
 */
export const UNDO_SECONDS = 30;
/** Clock grace on top: a slow network, or a phone clock a few seconds off the server's. */
export const UNDO_GRACE_SECONDS = 5;
/** The server accepts Undo this long after the tap (the window plus the grace), never longer. */
export const UNDO_SERVER_SECONDS = UNDO_SECONDS + UNDO_GRACE_SECONDS;
/** Records are kept at least 90 days (requirement rec-retention); a template may keep them longer. */
export const DEFAULT_RETENTION_DAYS = 90;

export type RevertReason = "mistap" | "unfinished" | "wrong_item";
export const REVERT_REASONS: readonly { key: RevertReason; label: string }[] = [
  { key: "mistap", label: "Tapped by mistake" },
  { key: "unfinished", label: "Wasn't finished" },
  { key: "wrong_item", label: "Wrong item" },
];
export const REVERT_NOTE_MAX = 300;

/** Appended to a record by "Not done after all". Set once; never changed or removed. */
export interface Correction {
  at: IsoDateTime;
  /** The account that corrected it. */
  by: Id;
  /** "undone": Undo inside the window (a void: hidden and not counted, but kept; `afterSeconds` says how soon). */
  reason: RevertReason | "undone";
  note?: string;
  /** Undo only: whole seconds between saving it and taking it back ("Undone by Sam 12 s later"). */
  afterSeconds?: number;
}

/** Undo inside the window never deletes (owner, 2026-10-09): the record is marked undone, who and how soon, and kept. */
export function undoneCorrection(o: { at: IsoDateTime; by: Id; savedAt: IsoDateTime }): Correction {
  const afterSeconds = Math.max(0, Math.round((Date.parse(o.at) - Date.parse(o.savedAt)) / 1000));
  return { at: o.at, by: o.by, reason: "undone", afterSeconds };
}
export const isUndone = (r: { correction?: Correction | null }): boolean => r.correction?.reason === "undone";

/** "Undone by Sam 12 s later" (the history and the audit trail). */
export function undoneLine(c: Correction, o: { name?: string } = {}): string {
  const s = c.afterSeconds;
  return `Undone by ${o.name || "someone"}${s === undefined ? "" : ` ${s} s later`}`;
}

/**
 * What can be checked off, by where it's stored:
 *  entry   a record (checklist item, one-tap check, pest All clear, a log)       log_entries
 *  place   Places "Mark cleaned / maintained"                                    activity_events
 *  quick   a quick log tap or a nudge's "I checked it"                          quick_logs
 *  order   Order "Mark it sent"                                                  supplier_orders
 *  arrival Order "Got it" (id = the order's id)                         supplier_orders.lines
 *  chore   a home chore's Mark done / Do it with me                              home_chore_done
 */
export type CheckoffKind = "entry" | "place" | "quick" | "order" | "arrival" | "chore";
export const CHECKOFF_KINDS: readonly CheckoffKind[] = ["entry", "place", "quick", "order", "arrival", "chore"];

/** What a check-off answers so the screen can offer Undo: which event, when, and until when. */
export interface CheckoffRef { kind: CheckoffKind; id: Id; at: IsoDateTime; undoUntil: IsoDateTime }

export function checkoffRef(kind: CheckoffKind, id: Id, at: IsoDateTime): CheckoffRef {
  return { kind, id, at, undoUntil: new Date(Date.parse(at) + UNDO_SECONDS * 1000).toISOString() };
}

/** Rows that still count as done (R3). */
export function live<T extends { correction?: Correction | null }>(rows: readonly T[]): T[] {
  return rows.filter((r) => !r.correction);
}
export const isCorrected = (r: { correction?: Correction | null }): boolean => !!r.correction;

export interface Refusal { code: "forbidden" | "conflict" | "invalid"; message: string; /** Undo refused: offer "Not done after all" instead. */ revert?: boolean }

/** Undo on screen: still inside the 30 s the toast shows (and never past the server's window). */
export function undoOnScreen(ref: Pick<CheckoffRef, "at">, now: number): boolean {
  return now < Date.parse(ref.at) + UNDO_SECONDS * 1000;
}

/** Why the server refuses Undo, or null (U1, U2). */
export function undoRefusal(item: { by: Id; at: IsoDateTime; correction?: Correction | null }, who: { userId: Id; now: Date | IsoDateTime; /** A manager or owner may undo a teammate's inside the window. */ role?: Role }): Refusal | null {
  if (item.correction) return { code: "conflict", message: isUndone(item) ? "This one was already undone." : "This one was already marked not done." };
  if (item.by !== who.userId && !(who.role && isManager(who.role))) return { code: "forbidden", message: "Only the person who saved it, or a manager, can undo it.", revert: true };
  const now = typeof who.now === "string" ? Date.parse(who.now) : who.now.getTime();
  if (now - Date.parse(item.at) > UNDO_SERVER_SECONDS * 1000) return { code: "conflict", message: "It's too late to undo this one; it's on the record. Use \"Not done after all\" instead.", revert: true };
  return null;
}

/** U3: a record whose side effects can't be taken back (a learning moment, an arrival) is corrected, not undone. */
export function entryUndoable(e: { templateId: string; evaluation: { ok: boolean } }): boolean {
  return e.evaluation.ok && e.templateId !== "receiving" && !NEVER_UNDO_TEMPLATES.has(e.templateId);
}
/**
 * Safety-critical submissions are never taken back by a shake or a toast (owner, 2026-10-09): an illness or injury report is
 * corrected through its own path. Sick and incident reports are stored as incidents, not log entries, and have no check-off
 * Undo at all (no route, no toast, no shake). The log entries that carry the same weight are listed here: cuts and burns, a
 * recall response, and ready-to-work (an ill worker's return). The daily health check-in IS undoable within the window
 * (a mis-tap), and stays in the audit trail.
 */
export const NEVER_UNDO_TEMPLATES: ReadonlySet<string> = new Set(["injury_response", "recall_response", "ready_to_work"]);

function isManager(role: Role) { return role === "owner" || role === "admin" || role === "manager"; }

/** Why "Not done after all" is refused, or null (R1, R2). `sameDay`: the item was done today, in the place's time. */
export function revertRefusal(
  item: { by: Id; at: IsoDateTime; correction?: Correction | null },
  who: { userId: Id; role: Role; now: Date | IsoDateTime; sameDay: boolean; retentionDays?: number },
): Refusal | null {
  if (item.correction) return { code: "conflict", message: "This one was already marked not done." };
  const now = typeof who.now === "string" ? Date.parse(who.now) : who.now.getTime();
  const ageDays = (now - Date.parse(item.at)) / 86_400_000;
  if (isManager(who.role)) {
    if (ageDays > (who.retentionDays ?? DEFAULT_RETENTION_DAYS)) return { code: "conflict", message: "This record is past its keeping period and can't be changed." };
    return null;
  }
  if (item.by !== who.userId) return { code: "forbidden", message: "Only the person who checked it off, or a manager, can mark it not done." };
  if (!who.sameDay) return { code: "forbidden", message: "You can mark your own check-offs not done on the same day. Ask a manager to correct an older one." };
  return null;
}

/** Can this person see "Not done after all" on this row? (The same rule the server applies.) */
export function canRevert(item: { by: Id; at: IsoDateTime; correction?: Correction | null }, who: { userId: Id; role: Role; now: Date | IsoDateTime; sameDay: boolean; retentionDays?: number }): boolean {
  return revertRefusal(item, who) === null;
}

/** Read and check { reason, note? } from a request body. Returns the correction fields or an error message. */
export function parseRevert(b: Record<string, unknown>): { reason: RevertReason; note?: string } | { error: string } {
  const reason = b.reason;
  if (typeof reason !== "string" || !REVERT_REASONS.some((r) => r.key === reason)) return { error: `"reason" must be one of: ${REVERT_REASONS.map((r) => r.key).join(", ")}.` };
  if (b.note !== undefined && b.note !== null && typeof b.note !== "string") return { error: '"note" must be text.' };
  const note = typeof b.note === "string" ? b.note.trim() : "";
  if (note.length > REVERT_NOTE_MAX) return { error: `"note" is at most ${REVERT_NOTE_MAX} characters.` };
  return { reason: reason as RevertReason, ...(note ? { note } : {}) };
}

export function reasonLabel(r: RevertReason | "undone"): string {
  return REVERT_REASONS.find((x) => x.key === r)?.label ?? r;
}

/** "Not done after all: Tapped by mistake. Sam, Sep 28 2:14 PM" (the words beside the struck original). */
export function correctionLine(c: Correction, o: { name?: string; timeLabel?: (iso: string) => string } = {}): string {
  if (c.reason === "undone") return undoneLine(c, o);
  const when = o.timeLabel ? o.timeLabel(c.at) : c.at.slice(0, 16).replace("T", " ");
  return `Not done after all: ${reasonLabel(c.reason)}${c.note ? ` (${c.note})` : ""}. ${[o.name, when].filter(Boolean).join(", ")}`;
}

/** Filter callback for rows typed without `correction` (Picks): true when the row still counts (R3). */
export const counts = (r: object): boolean => !(r as { correction?: unknown }).correction;
